import type { ComponentProps, MouseEvent } from 'react';
import React, { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import type { Nullable } from 'corgi-types';
import type { AdditionalServiceType, GQLLocale } from 'graphql-ts-types';
import { noop } from 'lodash-es';

import { getMessageFromError } from 'common/utils/error-handlers';
import type ArticlePreview from '../components/ArticlePreview';
import type { Assembly } from '../components/ArticlePreview/types';
import CartDrawerContents from '../components/CartDrawerContents';
import { DIGITAL_CONFIRMATION_PATH } from '../constants/checkout';
import cookieStorage from '../CookieStorage';
import { EventBusEvent } from '../enums/EventBus';
import eventBus from '../event-bus/EventBus';
import type {
  AddToCartV2MutationVariables,
  CartResponseFragment,
  ItemPriceV3Fragment,
  RemoveProductFromSaveForLaterMutation,
  RemoveProductFromSaveForLaterMutationVariables,
} from '../gql/generated/schema';
import { removeProductFromSaveForLaterMutation } from '../gql/mutation/cart/remove-saved-product';
import type { Cart, CartMessage } from '../gql/mutation/cart/types';
import type { Prefix } from '../providers/LocaleProvider';
import { useLocalization } from '../providers/LocaleProvider';
import type { AccessorySliderProps } from '../types/accessory-slider';
import type { ArticleMaterialType, ArticleTracking } from '../types/Article';
import type { DispatchState } from '../types/React';
import {
  dispatchAddToCartTrackingEvent,
  executeAddToCartV2,
  executeAddToDigitalCart,
  getAlertMessage,
} from '../utils/cart';
import { captureMessage } from '../utils/error-reporter';
import { isGiftCard } from '../utils/gift-cards';
import { getReCaptchaChallengeWithRetry, ReCaptchaAction } from '../utils/grecaptcha';
import { getRedirect } from '../utils/url';
import { useGetOpenDrawer } from './drawer';
import { useGQLLocale } from './locale';

interface BaseUseAddToCartSubmitParams extends Omit<
  AddToCartV2MutationVariables,
  'locale' | 'zipCode' | 'challenge' | 'item'
> {
  sku: string;
  article: Omit<ComponentProps<typeof ArticlePreview>, 'itemPrice_v3'> & {
    materialType?: ArticleMaterialType;
  };
  quantity: number;
  onSuccess?: (cart: Cart) => void;
  onError?: (err?: unknown) => void;
}

interface UseAddToCartSubmitParamsWithTracking extends BaseUseAddToCartSubmitParams {
  tracking: ArticleTracking;
  cartGrandTotal: number;
  assembly: Assembly;
}

type UseAddToCartSubmitParams = (
  | BaseUseAddToCartSubmitParams
  | UseAddToCartSubmitParamsWithTracking
) & {
  accessorySliderProps?: AccessorySliderProps;
  disableDrawerOpening?: boolean;
  selectedServices?: AdditionalServiceType[];
  couponCode?: string;
};

interface GetSubmitHandlerParams extends Pick<UseAddToCartSubmitParams, 'sku' | 'quantity'> {
  locale: GQLLocale;
  materialType: ArticleMaterialType;
  prefix: Prefix;
  setIsLoading: DispatchState<boolean>;
  selectedServices: AdditionalServiceType[];
  couponCode: string;
  handleSuccess: (params: { cart: Cart; messages: Nullable<CartMessage[]> }) => void;
  handleError: (error: unknown) => void;
}

const getCount = (items: Cart['items']) =>
  items.reduce((counter, { quantity: amount }) => counter + amount, 0);

const emitCounterUpdate = (items: Cart['items']) => {
  eventBus.emit(EventBusEvent.CartCounterUpdate, getCount(items));
};

type GetDrawerParams = Pick<UseAddToCartSubmitParams, 'article' | 'sku'> & {
  messages: CartMessage[];
  accessorySliderProps?: AccessorySliderProps;
  itemPrice_v3: Pick<ItemPriceV3Fragment, 'original' | 'final'>;
};
const getDrawerParams = ({ messages, ...restParams }: GetDrawerParams) => ({
  content: (
    <CartDrawerContents message={getAlertMessage(restParams.sku, messages)} {...restParams} />
  ),
  title: '',
});

type UseSuccessHandlerParams = Omit<
  UseAddToCartSubmitParams,
  'assemblyServiceAdded' | 'electronicsDisposalAdded' | 'couponCode'
> & { locale: GQLLocale };

const useSuccessHandler = (params: UseSuccessHandlerParams) => {
  const {
    quantity,
    disableDrawerOpening,
    onSuccess = noop,
    selectedServices,
    locale,
    ...restProps
  } = params;
  const openDrawer = useGetOpenDrawer();
  const client = useApolloClient();

  return async ({ cart, messages }: Pick<CartResponseFragment, 'cart' | 'messages'>) => {
    if (!disableDrawerOpening) {
      const itemPrice_v3 = cart.items.find(
        ({ article }) => article.sku === params.sku
      )?.itemPrice_v3;

      openDrawer(
        getDrawerParams({ messages: messages as CartMessage[], itemPrice_v3, ...restProps })
      );
    }

    try {
      await client.mutate<
        RemoveProductFromSaveForLaterMutation,
        RemoveProductFromSaveForLaterMutationVariables
      >({
        mutation: removeProductFromSaveForLaterMutation,
        variables: { sku: restProps.sku, locale },
      });
    } catch (error) {
      captureMessage(`Failed to remove the product from savedForLater list. ${error}`);
    }

    emitCounterUpdate(cart.items);
    onSuccess(cart);

    if (hasTracking(params)) {
      const cartItem = cart.items.find(({ article }) => article.sku === params.sku);

      dispatchAddToCartTrackingEvent({
        quantity,
        services: selectedServices,
        grandTotal: cart.grandTotal,
        tax: cartItem?.itemPrice_v3.tax,
        couponCode: cartItem?.couponCode,
        couponDiscountAmount: cartItem?.couponDiscountAmount,
        ...params,
      });
    }
  };
};

const hasTracking = (
  params: Omit<UseSuccessHandlerParams, 'locale'>
): params is UseAddToCartSubmitParamsWithTracking => 'tracking' in params;

type UseErrorHandlerOptions = Pick<UseAddToCartSubmitParams, 'onError'>;
const useErrorHandler = (params: UseErrorHandlerOptions) => {
  const { onError } = params;

  return (error: unknown) => {
    captureMessage(`Failed to add to cart. ${getMessageFromError(error)}`);
    onError(error);
  };
};

type GetRegularV2SubmitHandlerParams = Omit<GetSubmitHandlerParams, 'materialType' | 'prefix'>;

type GetGiftCardSubmitHandlerParams = Pick<
  GetSubmitHandlerParams,
  'sku' | 'quantity' | 'setIsLoading' | 'handleError'
> & {
  prefix: Prefix;
};
const getGiftCardSubmitHandler =
  ({ setIsLoading, handleError, prefix, ...restParams }: GetGiftCardSubmitHandlerParams) =>
  async (event: MouseEvent) => {
    const redirect = getRedirect(prefix(DIGITAL_CONFIRMATION_PATH));
    event.preventDefault();
    setIsLoading(true);

    try {
      const response = await executeAddToDigitalCart(restParams);
      if (response.data?.error) {
        throw response.data?.error;
      }

      redirect();
    } catch (error: unknown) {
      handleError(error);
      setIsLoading(false);
    }
  };

const getRegularV2SubmitHandler =
  ({
    setIsLoading,
    handleSuccess,
    handleError,
    selectedServices,
    couponCode,
    sku,
    quantity,
    locale,
  }: GetRegularV2SubmitHandlerParams) =>
  async (event: MouseEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const zipCode = cookieStorage.getZipCode();
      const challenge = await getReCaptchaChallengeWithRetry(ReCaptchaAction.AddToCart);
      const response = await executeAddToCartV2({
        locale,
        challenge,
        zipCode,
        item: { sku, quantity, services: selectedServices, couponCode },
      });

      if (!response.data || response.data.error || !response.data.cart) {
        return;
      }
      const { cart, messages = [] } = response.data;
      handleSuccess({ cart, messages: messages as CartMessage[] });
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

const getSubmitHandler = ({
  materialType,
  handleSuccess,
  prefix,
  locale,
  selectedServices,
  couponCode,
  ...restParams
}: GetSubmitHandlerParams) =>
  isGiftCard(materialType)
    ? getGiftCardSubmitHandler({ ...restParams, prefix })
    : getRegularV2SubmitHandler({
        ...restParams,
        locale,
        selectedServices,
        couponCode,
        handleSuccess,
      });

export const useAddToCartSubmit = ({
  sku,
  quantity,
  selectedServices,
  couponCode,
  ...restParams
}: UseAddToCartSubmitParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const { prefix } = useLocalization();
  const locale = useGQLLocale();
  const handleSuccess = useSuccessHandler({
    ...restParams,
    selectedServices,
    sku,
    quantity,
    locale,
  });
  const handleError = useErrorHandler(restParams);

  const { materialType } = restParams.article;

  const handler = useCallback(
    getSubmitHandler({
      sku,
      quantity,
      locale,
      prefix,
      materialType,
      selectedServices,
      couponCode,
      setIsLoading,
      handleSuccess,
      handleError,
    }),
    [setIsLoading, sku, quantity, selectedServices, couponCode, locale, restParams]
  );

  return { isLoading, handler };
};
